import {useMemo} from 'react';

import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {VoucherInvalidIcon} from 'assets/icons/voucherInvalid';
import Button from 'components/button/Button';
import {useAppDispatch, useAppSelector} from 'hooks';
import useGetAvailableVouchers from 'hooks/useGetAvailableVouchers';
import {useSelectedItemsLocalStorage} from 'hooks/useSelectedItemsLocalStorage';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {setNeedRetriggerCheckoutAction} from 'store/actions/app-action';
import {removePromotionVoucherAction} from 'store/actions/basket-action';

export type VoucherInvalidAlertContentProps = {
  onClose: () => void;
};

export const VoucherInvalidAlertContent = (props: VoucherInvalidAlertContentProps) => {
  const {onClose} = props;
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const {basket} = useAppSelector((state) => state?.basket);
  const details = useAppSelector((state) => state.alert.details);
  const selectedItemsLocalStorage = useSelectedItemsLocalStorage();
  const selectedItemSkus = useMemo(() => selectedItemsLocalStorage.get(), [selectedItemsLocalStorage]);
  const {availableVouchers} = useGetAvailableVouchers();
  const appliedVoucherPromotion = basket?.appliedVoucherPromotion;
  const basketTotalPrice = basket?.total || 0;
  const isReactNativeWebView = ReactNative.isReactNativeWebView();
  const selectedItemDetails = useMemo(() => {
    return basket?.basketItems.filter((item) => selectedItemSkus.includes(item.productDetails.id));
  }, [basket?.basketItems, selectedItemSkus]);

  const onSelectOther = () => {
    SendBrowserEvent.goToBasketVouchers(
      selectedItemDetails || [],
      appliedVoucherPromotion,
      availableVouchers,
      basketTotalPrice,
      selectedItemsLocalStorage.get()
    );
    onClose();
  };

  const retryCheckout = () => {
    dispatch(setNeedRetriggerCheckoutAction(details?.needTriggerCheckoutOnRetry || false));
  };

  const onContinueWithout = () => {
    dispatch(removePromotionVoucherAction({callback: retryCheckout}));
    onClose();
  };

  return (
    <Box flexDirection="column" display="flex" gap={2}>
      <Box alignItems="center" width="100%" display="flex" flexDirection="column" gap={1}>
        <VoucherInvalidIcon />
        <Typography color="#BA1A1A" fontSize={20} fontWeight={700} align="center">
          {t('alerts.invalidVoucher.title')}
        </Typography>
        <Typography fontSize={16} align="center">
          {t('alerts.invalidVoucher.description')}
        </Typography>
      </Box>
      {isReactNativeWebView && (
        <Button onClick={onSelectOther} shape="rounded" variant="contained">
          {t('alerts.invalidVoucher.selectOther')}
        </Button>
      )}
      <Button onClick={onContinueWithout} shape="rounded" variant="outlined">
        {t('alerts.invalidVoucher.continueWithout')}
      </Button>
    </Box>
  );
};
